import { UserMeeting, UserMeetingOwner, UserMeetingsPage } from '@/shared/api/user/types';

type UserMeetingDto = {
  id: string;
  title: null | string;
  end_at: null | string;
  outcome: null | string;
  owner: null | UserMeetingOwnerDto;
  start_at: null | string;
  updated_at: string;
};

type UserMeetingOwnerDto = {
  id: string;
  name: null | string;
};

type UserMeetingsPageDto = {
  hubspot_booking_confirmed: boolean;
  items: UserMeetingDto[];
  next_after: null;
  status_available: boolean;
};

const ADAPTER_ERROR_MESSAGE = 'User meeting data is unavailable';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const isNullableString = (value: unknown): value is null | string => value === null || typeof value === 'string';

const throwAdapterError = (): never => {
  throw Object.assign(new Error(ADAPTER_ERROR_MESSAGE), { status: 400 });
};

const adaptUserMeetingOwner = (value: unknown): UserMeetingOwner => {
  if (!isRecord(value) || !isNonEmptyString(value.id) || !isNullableString(value.name)) {
    return throwAdapterError();
  }

  const owner = value as UserMeetingOwnerDto;

  return { id: owner.id, name: owner.name };
};

const adaptUserMeeting = (value: unknown): UserMeeting => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    !isNullableString(value.title) ||
    !isNullableString(value.end_at) ||
    !isNullableString(value.outcome) ||
    !(value.owner === null || isRecord(value.owner)) ||
    !isNullableString(value.start_at) ||
    !isNonEmptyString(value.updated_at)
  ) {
    return throwAdapterError();
  }

  const meeting = value as UserMeetingDto;

  return {
    id: meeting.id,
    title: meeting.title,
    endAt: meeting.end_at,
    outcome: meeting.outcome,
    owner: meeting.owner === null ? null : adaptUserMeetingOwner(meeting.owner),
    startAt: meeting.start_at,
    updatedAt: meeting.updated_at,
  };
};

export const adaptUserMeetingsPage = (value: unknown): UserMeetingsPage => {
  if (
    !isRecord(value) ||
    typeof value.hubspot_booking_confirmed !== 'boolean' ||
    !Array.isArray(value.items) ||
    value.next_after !== null ||
    typeof value.status_available !== 'boolean'
  ) {
    return throwAdapterError();
  }

  const page = value as UserMeetingsPageDto;

  return {
    hubspotBookingConfirmed: page.hubspot_booking_confirmed,
    items: page.items.map(adaptUserMeeting),
    nextAfter: page.next_after,
    statusAvailable: page.status_available,
  };
};
